import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { authAPI } from '../services/api';
import './ForgotPasswordPage.css';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { password: '', confirmPassword: '' },
  });

  const password = watch('password');

  const onSubmit = async (data) => {
    if (!token) {
      toast.error('Invalid reset link. Please request a new one.');
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.resetPassword({
        token,
        password: data.password,
      });
      if (response.status) {
        toast.success(response.msg || 'Password updated successfully');
        setDone(true);
      } else {
        toast.error(response.msg || response.message || 'Failed to reset password.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="forgot-password-page">
        <Navbar />
        <Container className="forgot-password-container">
          <div className="forgot-password-success">
            <h2>Invalid link</h2>
            <p>This password reset link is invalid or has expired.</p>
            <div className="forgot-password-actions">
              <CustomButton variant="primary" onClick={() => navigate('/forgot-password')}>
                Request new link
              </CustomButton>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <Navbar />
      {loading && <LoadingSpinner fullScreen text="Updating password..." />}
      <Container className="forgot-password-container">
        <div className="forgot-password-header">
          <h1>Reset Password</h1>
          <p>Choose a new password for your account</p>
        </div>
        {!done ? (
          <div className="forgot-password-form-wrapper">
            <Form onSubmit={handleSubmit(onSubmit)} className="forgot-password-form">
              <div className="form-section">
                <label className="form-label-custom">New Password</label>
                <Form.Control
                  type="password"
                  placeholder="At least 6 characters"
                  className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                {errors.password && <div className="error-message">{errors.password.message}</div>}
              </div>
              <div className="form-section">
                <label className="form-label-custom">Confirm Password</label>
                <Form.Control
                  type="password"
                  placeholder="Re-enter password"
                  className={`form-input ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (v) => v === password || 'Passwords do not match',
                  })}
                />
                {errors.confirmPassword && (
                  <div className="error-message">{errors.confirmPassword.message}</div>
                )}
              </div>
              <CustomButton type="submit" variant="primary" fullWidth loading={loading} className="forgot-password-submit">
                Update Password
              </CustomButton>
              <div className="forgot-password-footer">
                <Link to="/signin">Back to Sign in</Link>
              </div>
            </Form>
          </div>
        ) : (
          <div className="forgot-password-success">
            <h2>Password updated</h2>
            <p>Your password has been changed. You can now sign in with your new password.</p>
            <div className="forgot-password-actions">
              <CustomButton variant="primary" onClick={() => navigate('/signin')}>
                Sign in
              </CustomButton>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ResetPasswordPage;
